import { useState } from "react";
import { Check, Loader2, X } from "lucide-react";
import { Button, Textarea } from "web-design-system";
import { handleError } from "web-core/utils";
import {
  usePortfolioReview,
  useSavePortfolioReviewRemarks,
} from "creso-core/hooks";

const PROMPTS = [
  "Comments about how the current portfolio is performing",
  "Comments about the changes made and where the portfolio would be heading",
];

// Matches the backend's limit.
const MAX_LENGTH = 5000;

// The distributor's note printed on the report. Reads the review from the
// cache so it shows what was last saved.
export function DistributorRemarksInput({ reviewId }: { reviewId: number }) {
  const { data: review } = usePortfolioReview(reviewId);
  const saved = review?.distributor_remarks ?? "";
  const [draft, setDraft] = useState(saved);
  const { mutate: save, isPending } = useSavePortfolioReviewRemarks();
  // The backend trims, so whitespace alone is no change.
  const unchanged = draft.trim() === saved;

  return (
    <div className="flex flex-col gap-4 rounded-lg border border-border bg-background p-4">
      <div className="relative">
        {draft ? null : (
          <ul
            aria-hidden
            className="pointer-events-none absolute inset-0 list-disc pl-5 text-sm text-muted-foreground"
          >
            {PROMPTS.map((prompt) => (
              <li key={prompt}>{prompt}</li>
            ))}
          </ul>
        )}
        <Textarea
          aria-label="Distributor remarks"
          value={draft}
          maxLength={MAX_LENGTH}
          disabled={isPending}
          onChange={(event) => setDraft(event.target.value)}
          className="relative min-h-32 resize-none border-0 bg-transparent p-0 text-sm shadow-none focus-visible:ring-0"
        />
      </div>
      <div className="flex justify-end gap-3">
        <Button
          variant="outline"
          size="icon"
          aria-label="Discard remarks"
          disabled={unchanged || isPending}
          onClick={() => setDraft(saved)}
        >
          <X />
        </Button>
        <Button
          size="icon"
          aria-label="Save remarks"
          disabled={unchanged || isPending}
          onClick={() =>
            save(
              { reviewId, remarks: draft },
              {
                onSuccess: (result) => setDraft(result.distributor_remarks),
                onError: (error) =>
                  handleError(error, "Could not save the remarks"),
              }
            )
          }
        >
          {isPending ? <Loader2 className="animate-spin" /> : <Check />}
        </Button>
      </div>
    </div>
  );
}
